import { QueryClient } from '@tanstack/react-query'
import { Issue } from '@/routes/projectStages/issue/types/issue'
import { IssueT } from '@/routes/projectStages/issues/lib/types/issuesTypes.ts'
import { getAssetUrl } from '@/config/assetUrl'
import {
  cacheAttachmentBlob,
  getCachedBlobUrl,
  recordWebAttachment,
} from './offlineAttachmentCache'
import { usePrefetchStore } from '@/store/usePrefetchStore'
import { isNativePlatform } from '@/lib/platform/platformDetection'

const FETCH_BATCH_SIZE = 5
const LARGE_FILE_THRESHOLD = 5 * 1024 * 1024 // 5 MB — process one at a time to limit memory
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB — matches offlineAttachmentCache limit

const CACHEABLE_MIME_PREFIXES = ['image/', 'video/'] as const
const CACHEABLE_MIME_TYPES = ['application/pdf'] as const

// Track cache.put failures by error name so we log each kind once per session
// instead of 2000+ times during a heavy prefetch.
const seenCachePutErrors = new Set<string>()
function logCachePutFailureOnce(error: unknown): void {
  const name = error instanceof Error ? error.name : 'unknown'
  if (seenCachePutErrors.has(name)) return
  seenCachePutErrors.add(name)
  console.warn('[prefetchAttachments] cache.put failed:', name, error)
}

function isCacheableMimeType(mimeType: string): boolean {
  return (
    CACHEABLE_MIME_PREFIXES.some(prefix => mimeType.startsWith(prefix)) ||
    CACHEABLE_MIME_TYPES.some(type => mimeType === type)
  )
}

/**
 * Yield to the main thread so the JS engine can run GC.
 */
function yieldToMain(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0))
}

/**
 * Build the set of issue IDs that belong to a given stage by walking that
 * stage's list caches in React Query.
 *
 * `singleIssue` query keys carry only the issue ID, so to scope attachment
 * collection to a single stage we must reconstruct stage→issues here.
 */
function collectIssueIdsForStage(queryClient: QueryClient, stageId: string): Set<string> {
  const ids = new Set<string>()
  const lists = queryClient.getQueriesData<IssueT[]>({
    queryKey: ['stageIssuesByIssueType', { projectStageId: stageId }],
  })
  for (const [, issues] of lists) {
    if (!issues) continue
    for (const issue of issues) {
      ids.add(String(issue.id))
    }
  }
  return ids
}

/**
 * Collect cacheable attachment URLs from cached issues in React Query.
 * For images, caches all three variants (small, medium, original) so the
 * list can render true thumbnails and the gallery modal can show full size.
 * Derivatives (small/medium) are always cached; the original is skipped when
 * the API-reported size exceeds MAX_FILE_SIZE_BYTES.
 * PDFs and videos use the original URL, skipped past the size limit.
 *
 * When `stageId` is given, only issues that belong to that stage's list
 * caches are scanned — needed for per-stage prefetch on the offline-cache
 * settings page.
 */
export function collectAttachmentUrls(queryClient: QueryClient, stageId?: string): string[] {
  const allIssueQueries = queryClient.getQueriesData<Issue>({
    queryKey: ['singleIssue'],
  })

  const allowedIds = stageId ? collectIssueIdsForStage(queryClient, stageId) : null

  const urls = new Set<string>()

  for (const [key, issue] of allIssueQueries) {
    if (!issue?.attachments) continue
    if (allowedIds) {
      const issueId = (key[1] as { id?: string | number } | undefined)?.id
      if (issueId == null || !allowedIds.has(String(issueId))) continue
    }

    for (const attachment of issue.attachments) {
      if (!attachment.mimeType || !isCacheableMimeType(attachment.mimeType)) continue

      const isImage = attachment.mimeType.startsWith('image/')
      const originalOverLimit = !!attachment.size && attachment.size > MAX_FILE_SIZE_BYTES

      if (isImage) {
        if (attachment.contentUrlSmall) urls.add(attachment.contentUrlSmall)
        if (attachment.contentUrlMedium) urls.add(attachment.contentUrlMedium)
        // contentUrlLarge is the lightbox/markup PRIMARY tier
        // (`contentUrlLarge ?? contentUrlMedium ?? contentUrl` — getGalleryItems.ts,
        // AttachmentsManager). The markup canvas <img> has NO size fallback, so
        // without prefetching this tier an already-uploaded photo opens BLANK in
        // the editor offline (the gallery <img> survives via its medium fallbackSrc,
        // which is why thumbnails render but markup doesn't). It's a small
        // derivative → always cache it.
        if (attachment.contentUrlLarge) urls.add(attachment.contentUrlLarge)
        if (attachment.contentUrl && !originalOverLimit) urls.add(attachment.contentUrl)
      } else if (attachment.contentUrl && !originalOverLimit) {
        urls.add(attachment.contentUrl)
      }
    }
  }

  return Array.from(urls)
}

/**
 * Cacheable attachment URLs for a SINGLE issue — the per-issue counterpart of
 * `collectAttachmentUrls`, used by cache-on-view. Same tier rules: all image
 * derivatives (small/medium/large) always, the original unless it's over the
 * size limit; PDFs/videos use the original.
 */
export function collectIssueAttachmentUrls(issue: Pick<Issue, 'attachments'>): string[] {
  const urls = new Set<string>()
  for (const attachment of issue.attachments ?? []) {
    if (!attachment.mimeType || !isCacheableMimeType(attachment.mimeType)) continue

    const isImage = attachment.mimeType.startsWith('image/')
    const originalOverLimit = !!attachment.size && attachment.size > MAX_FILE_SIZE_BYTES

    if (isImage) {
      if (attachment.contentUrlSmall) urls.add(attachment.contentUrlSmall)
      if (attachment.contentUrlMedium) urls.add(attachment.contentUrlMedium)
      if (attachment.contentUrlLarge) urls.add(attachment.contentUrlLarge)
      if (attachment.contentUrl && !originalOverLimit) urls.add(attachment.contentUrl)
    } else if (attachment.contentUrl && !originalOverLimit) {
      urls.add(attachment.contentUrl)
    }
  }
  return Array.from(urls)
}

/**
 * Cache one issue's attachments for offline use, on demand ("cache-on-view").
 *
 * NATIVE ONLY. The native offline attachment cache is populated solely by the
 * once-per-session prefetch, so a photo the user just created — or any issue
 * opened after that prefetch ran — is invisible offline until the NEXT
 * session's prefetch. Calling this when an issue is viewed online closes the
 * gap: view it once with a connection and it survives going offline. On web
 * the SW caches attachments on normal image load, so this is a no-op there.
 *
 * Best-effort: already-cached tiers are skipped (no re-fetch), and per-file
 * failures are swallowed — offline availability is non-critical and must never
 * disrupt viewing the issue.
 */
export async function cacheIssueAttachmentsForOffline(
  issue: Pick<Issue, 'attachments'> | null | undefined,
  stageId: string | undefined,
  signal: AbortSignal,
): Promise<void> {
  if (!issue || !stageId || !isNativePlatform()) return

  const urls = collectIssueAttachmentUrls(issue).filter(url => !getCachedBlobUrl(url))
  for (const url of urls) {
    if (signal.aborted) return
    try {
      await fetchAndCacheAttachment(url, signal, stageId)
    } catch {
      // best-effort — a failed cache-on-view just means the photo isn't
      // available offline yet; the next view (or prefetch) retries.
    }
  }
}

/**
 * Fetch a single attachment, validate its size, and cache it.
 * On native: persists to Capacitor Filesystem + Dexie index.
 * On web: writes the response into the SW's `media-uploads-cache` directly
 * via Cache API (rather than trusting the SW to intercept the fetch). This
 * eliminates the dev-mode / cross-origin gap we kept seeing where Dexie
 * recorded ~742 MB of downloads while `navigator.storage.estimate()`
 * showed only ~21 MB persisted — most responses weren't ending up in the
 * cache because the SW wasn't intercepting them.
 * Returns true if the file was cached, false if it was skipped.
 */
async function fetchAndCacheAttachment(
  relativeUrl: string,
  signal: AbortSignal,
  stageId: string,
): Promise<boolean> {
  const absoluteUrl = getAssetUrl(relativeUrl)
  const response = await fetch(absoluteUrl, { signal, credentials: 'include' })

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }

  // On web, clone the response BEFORE consuming the body so we can put it
  // into the cache. cache.put expects an unconsumed response.
  const responseForCache = isNativePlatform() ? null : response.clone()

  const blob = await response.blob()

  // Skip files that exceed the size limit
  // (API `size` field may be null, so we also check the actual blob)
  if (blob.size > MAX_FILE_SIZE_BYTES) return false

  if (isNativePlatform()) {
    await cacheAttachmentBlob(relativeUrl, blob, stageId)
  } else if (stageId) {
    // Explicitly persist into the SW's named cache. Bypasses any flakiness
    // in the SW interception path (dev mode, cross-origin requests, etc.)
    // — what we wrote here is exactly what the SW's CacheFirst route will
    // serve later when the page loads images.
    if (responseForCache && typeof caches !== 'undefined') {
      try {
        const cache = await caches.open('media-uploads-cache')
        await cache.put(absoluteUrl, responseForCache)
      } catch (error) {
        // Quota errors / opaque responses are non-fatal but we want to know
        // when this happens — silently swallowing here was hiding why
        // recorded Dexie size disagreed with `navigator.storage.estimate()`.
        // Throttled to once per session per error name to avoid log spam.
        logCachePutFailureOnce(error)
      }
    }
    await recordWebAttachment(relativeUrl, stageId, blob.size)
  }

  return true
}

/**
 * Filter out attachment URLs already present in the SW's media-uploads-cache.
 * Used on web where attachments are served by the SW, not from Capacitor Filesystem.
 *
 * The SW cache is keyed by absolute URLs (the browser resolves relative URLs
 * before the SW intercepts), so we must convert to absolute before matching.
 */
async function filterUncachedWebUrls(urls: string[]): Promise<string[]> {
  try {
    const cache = await caches.open('media-uploads-cache')
    const uncached: string[] = []
    for (const url of urls) {
      const match = await cache.match(getAssetUrl(url))
      if (!match) uncached.push(url)
    }
    return uncached
  } catch {
    return urls
  }
}

/**
 * Download attachments and cache them for offline use.
 * On native: writes to Capacitor Filesystem + Dexie index.
 * On web: fetch() triggers the SW's StaleWhileRevalidate to cache responses.
 *
 * Small files (< 5 MB) are fetched in parallel batches of FETCH_BATCH_SIZE.
 * Large files (>= 5 MB) are fetched sequentially to avoid the blob→base64
 * conversion from exhausting WKWebView's ~80-120 MB memory budget.
 */
export async function prefetchAttachments(
  queryClient: QueryClient,
  signal: AbortSignal,
  options: { stageId?: string } = {},
): Promise<void> {
  const { stageId } = options
  const allUrls = collectAttachmentUrls(queryClient, stageId)

  // Filter out already-cached URLs (native: in-memory urlMap, web: SW cache)
  const urlsToFetch = isNativePlatform()
    ? allUrls.filter(url => !getCachedBlobUrl(url))
    : await filterUncachedWebUrls(allUrls)

  if (urlsToFetch.length === 0) return

  // Partition into small and large files using API-reported size.
  // URLs with unknown size go into the small batch (most images are small).
  const sizeByUrl = buildSizeMap(queryClient, stageId)
  const smallUrls: string[] = []
  const largeUrls: string[] = []

  for (const url of urlsToFetch) {
    const size = sizeByUrl.get(url)
    if (size && size >= LARGE_FILE_THRESHOLD) {
      largeUrls.push(url)
    } else {
      smallUrls.push(url)
    }
  }

  const totalCount = smallUrls.length + largeUrls.length

  usePrefetchStore.setState({
    totalAttachments: totalCount,
    completedAttachments: 0,
  })

  // stageId is required for native cache writes (Dexie schema). Web ignores
  // it but we still pass a stable string so the signature is uniform.
  const ownerStageId = stageId ?? ''

  let completed = 0
  const baseErrorCount = usePrefetchStore.getState().errorCount
  let attachmentErrors = 0

  // Small files: parallel batches
  for (let i = 0; i < smallUrls.length; i += FETCH_BATCH_SIZE) {
    if (signal.aborted) return

    const batch = smallUrls.slice(i, i + FETCH_BATCH_SIZE)
    const results = await Promise.allSettled(
      batch.map(url => fetchAndCacheAttachment(url, signal, ownerStageId)),
    )

    for (const result of results) {
      if (result.status === 'rejected') attachmentErrors++
    }

    completed += batch.length
    usePrefetchStore.setState({
      completedAttachments: completed,
      errorCount: baseErrorCount + attachmentErrors,
    })

    if (i + FETCH_BATCH_SIZE < smallUrls.length) {
      await yieldToMain()
    }
  }

  // Large files: sequential to limit peak memory
  for (const url of largeUrls) {
    if (signal.aborted) return

    try {
      await fetchAndCacheAttachment(url, signal, ownerStageId)
    } catch {
      attachmentErrors++
    }

    completed += 1
    usePrefetchStore.setState({
      completedAttachments: completed,
      errorCount: baseErrorCount + attachmentErrors,
    })
    await yieldToMain()
  }
}

/**
 * Build a map of attachment URL → file size from the React Query cache.
 * Used to partition URLs into small/large for memory-safe batching.
 * Only the original `contentUrl` is mapped with the API-reported size —
 * small/medium derivatives are always well under the threshold and stay in
 * the small-batch path regardless of the original's size.
 */
function buildSizeMap(queryClient: QueryClient, stageId?: string): Map<string, number> {
  const allIssueQueries = queryClient.getQueriesData<Issue>({
    queryKey: ['singleIssue'],
  })

  const allowedIds = stageId ? collectIssueIdsForStage(queryClient, stageId) : null

  const sizeMap = new Map<string, number>()

  for (const [key, issue] of allIssueQueries) {
    if (!issue?.attachments) continue
    if (allowedIds) {
      const issueId = (key[1] as { id?: string | number } | undefined)?.id
      if (issueId == null || !allowedIds.has(String(issueId))) continue
    }

    for (const attachment of issue.attachments) {
      if (!attachment.size || !attachment.contentUrl) continue
      sizeMap.set(attachment.contentUrl, attachment.size)
    }
  }

  return sizeMap
}
